import { useMemo, useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  api,
  streamSwitch,
  type AppSummary,
  type Environment,
  type SwitchEvent,
} from "@/lib/api";
import { FLY_REGIONS, regionLabel } from "@/lib/regions";
import { cn } from "@/lib/utils";

export function RegionSwitcher({ apps }: { apps: AppSummary[] }) {
  const [env, setEnv] = useState<Environment>("stable");
  const [target, setTarget] = useState<string>("fra");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [events, setEvents] = useState<SwitchEvent[]>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const envApps = useMemo(() => apps.filter((a) => a.env === env), [apps, env]);
  const currentRegions = useMemo(() => Array.from(new Set(envApps.map((a) => a.region))), [envApps]);
  const sameRegion = currentRegions.length === 1 && currentRegions[0] === target;

  async function start() {
    setEvents([]);
    setError(null);
    setRunning(true);
    setConfirmOpen(false);
    try {
      const { job_id } = await api.startSwitch({ env, target_region: target });
      await streamSwitch(
        job_id,
        (ev) => setEvents((prev) => [...prev, ev]),
        () => setRunning(false),
        () => {
          setError("connection to event stream dropped");
          setRunning(false);
        },
      );
    } catch (e) {
      setError(String(e));
      setRunning(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Switch region</CardTitle>
        <CardDescription>
          Moves the {env} pair (<span className="font-mono">{envApps.map((a) => a.name).join(", ")}</span>) together.
          Volume forks take a few minutes; both apps will be briefly unavailable while their old machines stop.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">env</label>
            <Select value={env} onValueChange={(v) => setEnv(v as Environment)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="stable">stable</SelectItem>
                <SelectItem value="prod">prod</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">current</label>
            <div className="h-9 flex items-center gap-1 px-3 text-sm font-mono text-[var(--color-muted)]">
              {currentRegions.length === 0
                ? "—"
                : currentRegions.map((r, i) => (
                    <span key={r ?? i}>
                      {r ?? "?"}
                      {i < currentRegions.length - 1 && <span className="opacity-40">, </span>}
                    </span>
                  ))}
            </div>
          </div>

          <ArrowRight className="h-5 w-5 text-[var(--color-muted)] mb-2" />

          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">target</label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="w-72">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FLY_REGIONS.map((r) => (
                  <SelectItem key={r.code} value={r.code}>
                    {r.code} — {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            disabled={running || sameRegion}
            onClick={() => setConfirmOpen(true)}
            className="ml-auto"
          >
            {running ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> switching…
              </>
            ) : (
              <>switch both → {target}</>
            )}
          </Button>
        </div>

        {(events.length > 0 || error) && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wide text-[var(--color-muted)] mb-2">log</div>
            <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] font-mono text-xs max-h-72 overflow-y-auto">
              {events.map((e, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex gap-2 px-3 py-1.5 border-b border-[var(--color-border)]/50 last:border-0",
                    e.status === "error" && "text-[var(--color-bad)]",
                    e.status === "ok" && e.step === "done" && "text-[var(--color-good)]",
                  )}
                >
                  <Badge variant="outline" className="font-mono text-[10px] shrink-0">
                    {e.app}
                  </Badge>
                  <span className="text-[var(--color-muted)] w-44 shrink-0">{e.step}</span>
                  <span className="w-12 shrink-0">{e.status}</span>
                  <span className="truncate">{e.detail}</span>
                </div>
              ))}
              {error && <div className="px-3 py-2 text-[var(--color-bad)]">{error}</div>}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Switch {env} to {target}?</DialogTitle>
            <DialogDescription>
              This will fork each volume to {regionLabel(target)}, clone each machine into the new
              region, and destroy the old machines + volumes once the new volumes finish hydrating.
              Both apps will be briefly unavailable. Recovery uses the old volumes' retained daily
              snapshots and requires their volume and snapshot IDs.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm mt-3 space-y-1">
            {envApps.map((a) => (
              <div key={a.name} className="flex items-center justify-between font-mono">
                <span>{a.name}</span>
                <span className="text-[var(--color-muted)]">{a.region ?? "?"} → {target}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">cancel</Button>
            </DialogClose>
            <Button variant="destructive" onClick={start}>do it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
